/* eslint-disable react/no-danger */
import React, { useEffect, useState } from "react"

import { PageSection } from "@components/sections"
import { CONSTANTS } from "@data"
import { Eyebrow, GridLines, Headline, ContentRow, CTA } from "@shared"
import { css, logTool } from "@jsUtils"
import Styles from "./herogrid.module.scss"

const { PAGE_NAMES, MISC } = CONSTANTS
const { EXO_TEXT_ANIMATE: enableExoAnimation } = MISC
const log = logTool(true)

/**
 * Detect parts delineated with asterisks (*) and wrap them in a snap for colorized styling
 * @param {*} text
 */
const formatHeaderText = text => {
  if (!text) {
    return null
  }

  let formattedResult = text
  const delineator = "*"
  let openIndex
  const pairs = []

  for (let i = 0; i < text.length; i++) {
    const char = text[i]
    if (char === delineator) {
      if (typeof openIndex === "undefined") {
        openIndex = i
      } else {
        pairs.push([openIndex, i])
        openIndex = undefined
      }
    }
  }

  pairs.forEach(pair => {
    const start = pair[0]
    const end = pair[1]

    formattedResult = `${formattedResult.substring(0, start)}<em>${formattedResult.substring(
      start + 1,
      end
    )}</em>${formattedResult.substring(end + 1)}`
  })

  return formattedResult
}

/**
 * Detect parts delineated with pipe (|) character and wrap them for animation
 * @param {*} text
 * @param {*} optionCount
 * @param {*} activeIndex
 */
const formatSubhead = (text, optionCount, activeIndex) => {
  const headlineParts = text.split("|")
  const headlineOptions = headlineParts.map((part, index) => {
    if (index === 0 || index === headlineParts.length - 1) {
      return ""
    }

    const lastIndex = activeIndex === optionCount - 1 ? 0 : activeIndex + 1
    return (
      <span
        className={css(
          Styles.subheadOption,
          index - 1 === activeIndex && Styles.active,
          index - 1 === lastIndex && Styles.last
        )}
        key={part}
      >
        {part}
      </span>
    )
  })

  const formattedHeadline = (
    <>
      {headlineParts[0]}
      <div className={Styles.subheadOptions}>{headlineOptions}</div>
      {headlineParts[headlineParts.length - 1]}
    </>
  )

  return formattedHeadline
}

let textAnimationTimeout = null

/**
 * New version of Hero component
 * Objectives
 *  - use consistent field names even if the incoming data does not
 *  - all fields come in like `field = { text, className, level }` for consistency etc.
 *  - wrap children for one-off scenarios
 * @param {object} props react props
 * @param {*} props.children - react children
 * @param {string} props.color - color set from use or contentful
 * @param {string} props.theme - theme set from use or contentful
 * @param {object} props.analytics - analytics properties to cascade down
 * @param {boolean} props.shortHeroSection - boolean passed in for optional shorter hero
 * @param {boolean} props.largeBaseHeroSection - boolean passed in for larger H1 like on home page
 * @param {object} props.content - contentful content - see herogrid.factory as noted below
 * @see herogrid.factory.js
 * @todo  change gridName to constant
 */
const HeroGrid = props => {
  const {
    children,
    color,
    theme,
    analytics,
    shortHeroSection = false,
    largeBaseHeaderSection = false,
    content,
    page
  } = props
  const { eyebrow, headerText, inlineHeader, subhead, copy, cta, miscClassNames } = content
  const {
    extraHeroSection,
    extraHeroWrapperRow,
    extraPageHeadingContent,
    childWrapper
  } = miscClassNames

  const [ready, setReady] = useState(false)

  const headerTextFormatted = formatHeaderText(headerText?.text)

  const [subheadPartActive, setSubheadPartActive] = useState(0)
  const subheadOptionCount = subhead?.text ? subhead.text.split("|").length - 2 : 0
  const subheadFormatted =
    subhead?.text && page === PAGE_NAMES.EXO_WORKS
      ? formatSubhead(subhead.text, subheadOptionCount, subheadPartActive)
      : subhead?.text

  useEffect(() => {
    if (subhead?.text && page === PAGE_NAMES.EXO_WORKS && enableExoAnimation) {
      clearTimeout(textAnimationTimeout)
      textAnimationTimeout = setTimeout(() => {
        if (subheadPartActive < subheadOptionCount - 1) {
          setSubheadPartActive(subheadPartActive + 1)
        } else {
          setSubheadPartActive(0)
        }
      }, 2000)
    }
  }, [subheadPartActive])

  return (
    <PageSection
      theme={theme}
      ready={ready}
      className={css(
        ready && Styles.ready,
        Styles.heroSection,
        shortHeroSection && Styles.shortHeroSection,
        !largeBaseHeaderSection && Styles.smallBaseHeaderSection,
        largeBaseHeaderSection && Styles.largeBaseHeaderSection,
        extraHeroSection
      )}
      onReady={() => {
        setReady(true)
      }}
    >
      <GridLines theme={theme} />
      {children && (
        <div className={css(Styles.heroChildren, childWrapper)}>
          {React.Children.map(children, (child, i) => {
            return React.cloneElement(child, {
              content,
              index: i
            })
          })}
        </div>
      )}
      <ContentRow
        tagname="div"
        gridName="mainHeroGrid"
        extraClass={css(Styles.heroWrapperRow, extraHeroWrapperRow)}
      >
        <div
          className={css(
            ready && Styles.ready,
            Styles.pageHeadingContentColumn,
            extraPageHeadingContent
          )}
        >
          {eyebrow && (
            <Eyebrow className={css(Styles.eyebrow, eyebrow.className)} color={color}>
              {eyebrow.text}
            </Eyebrow>
          )}
          <div className={headerText && subheadFormatted ? Styles.headingPair : ""}>
            {headerText && (
              <Headline
                size={headerText.level}
                emColor={color}
                className={css(Styles.headerText, headerText.className)}
              >
                {headerTextFormatted && (
                  <span dangerouslySetInnerHTML={{ __html: headerTextFormatted }} />
                )}
                {inlineHeader && (
                  <>
                    <span className={Styles.wedge}>-</span>
                    <span className={css(Styles.inline, inlineHeader.className)}>
                      {inlineHeader.text}
                    </span>
                  </>
                )}
              </Headline>
            )}
            {subheadFormatted && (
              <Headline
                className={css(Styles.subhead, subhead.className)}
                size={subhead.level}
                subhead={subheadFormatted}
              >
                {subheadFormatted}
              </Headline>
            )}
          </div>
          {copy && <p className={css(Styles.description, copy.className)}>{copy.text}</p>}
          {cta && cta.link && (
            <CTA
              {...analytics}
              to={cta.link}
              color={color}
              ready
              className={css(Styles.cta, ready && Styles.ready, cta.className)}
            >
              {cta.text}
            </CTA>
          )}
        </div>
      </ContentRow>
    </PageSection>
  )
}

export default HeroGrid
